// ── Where it's playing ───────────────────────────────────────────────────────
// Pure shaping for the "Where it's playing" sheet (17.165). Input: one TMDB US
// region payload per season. Output: a season marquee (one row per season, your
// services flagged), a one-line headline about the season you're on, and the
// migration story ("Netflix carried S1–S4 · Philo since S5"). No React, no I/O.

const names = (list) => (list || []).map(p => p.provider_name)

// Collapse TMDB's tiered duplicates ("Netflix Standard with Ads", "Paramount+
// Apple TV Channel") onto one tile per service so a row reads as a marquee, not a
// price list. Registry services match by id; the rest keep TMDB's name.
function tile(p, myIds, registry) {
  const svc = (registry || []).find(s => s.id === p.provider_id) || null
  return { id: p.provider_id, name: svc?.name || p.provider_name, logo: p.logo_path || null, mine: myIds.has(p.provider_id), svc }
}
function dedupe(tiles) {
  const seen = new Map()
  for (const t of tiles) {
    const k = t.name.replace(/\s*(standard )?(with ads|premium|essential|basic).*$/i, '').replace(/\s*(apple tv|amazon|roku)\s*channel$/i, '').trim().toLowerCase()
    const prev = seen.get(k)
    if (!prev || (t.svc && !prev.svc) || (t.mine && !prev.mine)) seen.set(k, { ...t, key: k })
  }
  return [...seen.values()]
}

export function seasonRow(season, region, myIds, registry) {
  const my = myIds instanceof Set ? myIds : new Set(myIds || [])
  const stream = dedupe((region?.flatrate || []).map(p => tile(p, my, registry)))
  const free = dedupe([...(region?.free || []), ...(region?.ads || [])].map(p => tile(p, my, registry)))
    .filter(f => !stream.some(s => s.key === f.key))
  // Your services first, then the registry's order, then the rest.
  const rank = t => (t.mine ? 0 : t.svc ? 1 : 2)
  stream.sort((a, b) => rank(a) - rank(b))
  return { season, stream, free, mine: stream.filter(t => t.mine), empty: stream.length + free.length === 0 }
}

export function buildMarquee({ seasons, regions, myIds, registry }) {
  const my = new Set(myIds || [])
  const rows = (seasons || []).map(n => seasonRow(n, regions?.[n] || null, my, registry))
  return rows
}

const list = (arr) => arr.length <= 1 ? arr.join('') : arr.length === 2 ? `${arr[0]} and ${arr[1]}` : `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`

// One sentence about the season you're on.
export function headline(row) {
  if (!row) return 'Still looking.'
  const s = `Season ${row.season}`
  if (row.mine.length) return `${s} is on ${list(row.mine.map(t => t.name))} — that's yours.`
  if (row.stream.length === 1) return `${s} is only on ${row.stream[0].name}.`
  if (row.stream.length) return `${s} is on ${list(row.stream.slice(0, 3).map(t => t.name))}${row.stream.length > 3 ? ' and more' : ''}.`
  if (row.free.length) return `${s} is free with ads on ${list(row.free.slice(0, 2).map(t => t.name))}.`
  return `${s} isn't streaming anywhere yet.`
}

// "Philo has every season · Hulu has S1 and S6 · Netflix has S4 only": how the
// show is spread across services, most complete first. Null when one service
// carries every season on its own (nothing to tell) or there is only one row.
export function migration(rows) {
  if (!rows || rows.length < 2) return null
  const by = new Map()
  for (const r of rows) for (const t of r.stream) {
    const e = by.get(t.key) || { name: t.name, seasons: [], mine: t.mine, svc: !!t.svc }
    e.seasons.push(r.season); by.set(t.key, e)
  }
  const all = rows.length
  // Real services (the registry) before add-on channels; fullest coverage first.
  const entries = [...by.values()].sort((a, b) => (b.svc - a.svc) || b.seasons.length - a.seasons.length || (b.mine - a.mine))
  if (!entries.length) return null
  if (entries[0].seasons.length === all && entries.length === 1) return null
  const spans = (ns) => {
    const out = []; let i = 0
    while (i < ns.length) { let j = i; while (j + 1 < ns.length && ns[j + 1] === ns[j] + 1) j++; out.push(j - i >= 1 ? `S${ns[i]}–S${ns[j]}` : `S${ns[i]}`); i = j + 1 }
    return out
  }
  const say = (e) => e.seasons.length === all ? `${e.name} has every season`
    : e.seasons.length === 1 ? `${e.name} has S${e.seasons[0]} only`
    : `${e.name} has ${list(spans(e.seasons))}`
  return entries.slice(0, 3).map(say).join(' · ')
}

// The quieter tier under the marquee.
export function freeLine(row) {
  if (!row?.free?.length) return null
  const lib = row.free.filter(t => /hoopla|kanopy/i.test(t.name)).map(t => t.name)
  const ads = row.free.filter(t => !/hoopla|kanopy/i.test(t.name)).map(t => t.name)
  const parts = []
  if (ads.length) parts.push(`Free with ads on ${list(ads.slice(0, 3))}`)
  if (lib.length) parts.push(`${ads.length ? 'library' : 'Library'} card on ${list(lib)}`)
  return parts.join(' · ')
}

// ONE definition of "the season you're on" for every surface that asks (the deck
// pill, useUnpark, the sheet): your position's season, else S1 — the card says
// "start from the beginning", so the sheet and the park check must agree with it.
// (17.167: the sheet defaulted to the LATEST season, said "S7 is only on Philo",
// offered to park, and useUnpark — reading S1, on Prime — un-parked it at once.)
export function seasonOn(progress) {
  const n = Number(progress?.season)
  return Number.isFinite(n) && n >= 1 ? n : 1
}
